import { inject, Injectable } from '@angular/core';
import { produce } from 'immer';

import { type CanvasElement } from '@blaze-runtime/engine-domain';
import { type Connection, MAIN_INPUT_NAME, type Workflow } from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../../+state';
import { type ButtonElement } from '../../elements/button/button.element';
import {
  getOrCreateConnection,
  getUiRuntimeElement,
  getWorkflowInputName,
  getWorkflowRuntimeElement,
} from '../../utils/runtime';

export interface ActionButtonConnections {
  buttonConnection: Connection;
  containerConnection: Connection;
  /** Only set when the workflow gained an input and therefore has to be saved. */
  workflow: Workflow | undefined;
}

/**
 * Connects an action button, and the container it sits in, to the workflow the button runs.
 *
 * The workflow needs both: the container says which row the action is about, the button says when
 * to run. Each arrives under a name of its own, so the workflow's author can tell them apart.
 */
@Injectable({ providedIn: 'root' })
export class ActionButtonConnectionsService {
  private readonly runtimeFacade = inject(RuntimeFacade);

  /** @returns nothing when the button runs no workflow, or when that workflow is not in the state. */
  createOrUpdateActionButtonConnections(
    connections: Connection[],
    container: CanvasElement,
    button: ButtonElement,
  ): ActionButtonConnections | undefined {
    const workflowId = button.props?.content?.action?.workflowId;
    if (!workflowId) {
      return undefined;
    }
    const workflow = this.runtimeFacade.workflowsValue.find((wf) => wf.id === workflowId);
    if (!workflow) {
      return undefined;
    }

    const containerConnection = getOrCreateConnection(
      connections,
      container.ns,
      getUiRuntimeElement(container.id),
      getWorkflowRuntimeElement(workflowId),
      'change',
    );
    const buttonConnection = getOrCreateConnection(
      connections,
      container.ns,
      getUiRuntimeElement(button.id),
      getWorkflowRuntimeElement(workflowId),
      'click',
    );

    let workflowUpdated = false;
    const wf = produce(workflow, (draft) => {
      const currentInputs = Object.entries(workflow.inputs);
      workflowUpdated = this.ensureNamedInputMutably(
        draft,
        currentInputs,
        container,
        containerConnection.id,
      );
      // NOTE the `||` order is intentional: the button's input must be ensured either way.
      workflowUpdated =
        this.ensureNamedInputMutably(draft, currentInputs, button, buttonConnection.id) ||
        workflowUpdated;
    });

    return {
      containerConnection,
      buttonConnection,
      workflow: workflowUpdated ? wf : undefined,
    };
  }

  /**
   * Gives the element's connection a name of its own on the workflow, replacing the unnamed input
   * an older workflow may carry.
   *
   * @returns whether the workflow changed.
   */
  private ensureNamedInputMutably(
    workflow: Workflow,
    currentInputs: [string, string][],
    element: CanvasElement,
    elementConnectionId: string,
  ): boolean {
    let existing = currentInputs.find(
      ([name, connectionId]) => connectionId === elementConnectionId && name !== MAIN_INPUT_NAME,
    );
    if (existing && existing[0] === '') {
      delete workflow.inputs[''];
      existing = undefined;
    }
    if (!existing) {
      workflow.inputs[getWorkflowInputName(element)] = elementConnectionId;
      return true;
    }
    return false;
  }
}
